import type { ReactNode } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { MediaView } from './MediaView';
import { founder } from '../../data/content';
import { ease } from '../../core/format/motion';
import { heroTitle } from '../../ui/sections';
import type { Media } from '../../core/types/media';
type Props = {
  /** Id van de h1, voor `aria-labelledby` van de sectie. */
  titleId: string;
  eyebrow?: string;
  /** De kop zelf (meestal `<HeadingText heading={…} accentBlock />`). */
  title: ReactNode;
  body?: string;
  /** Knoppen onder de tekst (gebruik `btnPrimaryLg` / `btnSecondaryLg`). */
  actions?: ReactNode;
  /** Een rustige regel onder de knoppen, gescheiden door een haarlijn. */
  footnote?: ReactNode;
  media: Media;
  /** Wie erachter zit, ín het beeld: twee korte regels naast Sarahs portret. */
  byline?: {
    lead: ReactNode;
    sub?: ReactNode;
  };
};

/**
 * De hero van de inhoudspagina's (salon, protocollen, over): één opbouw, één maat kop, één manier
 * van bewegen (docs/VORMGEVING.md §3).
 *
 * - Computer: tekst links op de inhoudsrand, beeld rechts tot de rand van het scherm.
 * - Telefoon: beeld bovenaan (40% van het scherm, nooit schermvullend), daaronder de tekst; beeld,
 *   kop en hoofdknop staan samen in het eerste scherm.
 * - Het beeld laadt met voorrang en beweegt niet bij binnenkomst (snelle eerste weergave). Alleen
 *   de tekst en het naambordje komen kort omhoog; bij "minder beweging" staat alles meteen.
 */
export function PageHero({
  titleId,
  eyebrow,
  title,
  body,
  actions,
  footnote,
  media,
  byline
}: Props) {
  const reduce = useReducedMotion();
  const rise = (delay: number) => reduce ? {} : {
    initial: {
      opacity: 0,
      y: 8
    },
    animate: {
      opacity: 1,
      y: 0
    },
    transition: {
      duration: 0.26,
      delay,
      ease
    }
  };
  return <section className="bg-sand" aria-labelledby={titleId} data-id="element-8712" data-mp="vcki9hjnl1rr">
      <div className="grid lg:min-h-[min(calc(100svh-108px),54rem)] lg:grid-cols-2" data-id="element-8713" data-mp="2toj1mry13mow">
        <div className="relative h-[40svh] max-h-[460px] min-h-[280px] overflow-hidden bg-blush sm:h-[52svh] sm:max-h-[560px] lg:order-2 lg:h-auto lg:max-h-none" data-id="element-8714" data-mp="2ppadq3po6j19">
          <MediaView media={media} priority sizes="(min-width: 1024px) 50vw, 100vw" controlsPosition="top-right" className="absolute inset-0 h-full w-full" data-id="element-8715" data-mp="rs0hl3l8pwg0" />
          {byline && <motion.div {...rise(0.2)} className="absolute bottom-4 left-4 flex max-w-[calc(100%-2rem)] items-center gap-3 bg-shell py-2.5 pl-2.5 pr-5 sm:bottom-6 sm:left-6 lg:bottom-10 lg:left-10" data-id="element-8716" data-mp="dz2ip79l9m7p">
              {/* Decoratief: haar naam staat ernaast. */}
              <img src={founder.portrait} alt="" width={44} height={44} className="h-11 w-11 shrink-0 rounded-full object-cover object-top" data-id="element-8717" data-mp="1aos89y343du3" />
              <p className="text-[13px] leading-snug text-muted" data-id="element-8718" data-mp="11pomgmck8kbw">
                {byline.lead}
                {byline.sub && <span className="block" data-id="element-8719" data-mp="3hgt1h11xbogo">{byline.sub}</span>}
              </p>
            </motion.div>}
        </div>

        <div className="flex flex-col justify-center px-6 pb-12 pt-7 sm:px-10 sm:pt-10 lg:order-1 lg:py-14 lg:pl-[max(2.5rem,calc((100vw-80rem)/2+2.5rem))] lg:pr-16 xl:pr-24" data-id="element-8720" data-mp="3mi06wtglgbk6">
          {eyebrow && <motion.p {...rise(0)} className="text-[14px] text-clay" data-id="element-8721" data-mp="3a05zn7gb659u">
              {eyebrow}
            </motion.p>}
          <motion.h1 {...rise(0.05)} id={titleId} className={`${eyebrow ? 'mt-4 lg:mt-5' : ''} ${heroTitle}`} data-id="element-8722" data-mp="3iz14og7m99bk">
            {title}
          </motion.h1>
          {body && <motion.p {...rise(0.12)} className="mt-5 max-w-[34rem] text-[17px] leading-relaxed text-muted lg:mt-6" data-id="element-8723" data-mp="2v742lgtyxvnn">
              {body}
            </motion.p>}
          {actions && <motion.div {...rise(0.17)} className="mt-7 flex flex-col gap-3 sm:flex-row sm:flex-wrap lg:mt-9" data-id="element-8724" data-mp="351354gxyfhg4">
              {actions}
            </motion.div>}
          {footnote && <motion.div {...rise(0.22)} className="mt-8 max-w-[34rem] border-t border-ink/15 pt-5 text-[14px] leading-relaxed text-muted" data-id="element-8725" data-mp="2cgmexqnztq95">
              {footnote}
            </motion.div>}
        </div>
      </div>
    </section>;
}